import { ApolloClient, InMemoryCache, createHttpLink, ApolloLink } from '@apollo/client'
import { onError } from '@apollo/client/link/error'
import { Sentry } from '@lib/sentry/sentry'

const GRAPHQL_URL = import.meta.env.VITE_MARKETPLACE_BACKEND_URL ?? 'http://localhost:4000/graphql'

const httpLink = createHttpLink({
  uri: GRAPHQL_URL,
  credentials: 'include',
})

// Error link to capture GraphQL errors in Sentry
const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, locations, path, extensions }) => {
      const errorMessage = `[GraphQL error]: Message: ${message}, Location: ${JSON.stringify(locations)}, Path: ${path}`

      console.error(errorMessage)

      // Capture to Sentry with context
      Sentry.captureException(new Error(errorMessage), {
        level: 'error',
        tags: {
          errorType: 'graphql',
          operationName: operation.operationName,
          operationType: operation.query.definitions[0]?.kind || 'unknown',
        },
        extra: {
          query: operation.query.loc?.source.body,
          variables: operation.variables,
          extensions,
          path,
        },
      })
    })
  }

  if (networkError) {
    console.error(`[Network error]: ${networkError}`)

    // Capture network errors
    Sentry.captureException(networkError, {
      level: 'error',
      tags: {
        errorType: 'network',
        operationName: operation.operationName,
      },
      extra: {
        query: operation.query.loc?.source.body,
        variables: operation.variables,
      },
    })
  }
})

const cache = new InMemoryCache({
  typePolicies: {
    Meeting: {
      keyFields: ['meetingId']
    },
    Attendee: {
      keyFields: ['attendeeId']
    }
  }
})

export const apolloClient = new ApolloClient({
  link: ApolloLink.from([errorLink, httpLink]),
  cache,
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'cache-and-network',
      errorPolicy: 'all',
    },
    query: {
      fetchPolicy: 'network-only',
      errorPolicy: 'all',
    },
    mutate: {
      errorPolicy: 'all',
    },
  },
})
